import { FC, MouseEvent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { RndResizeCallback } from 'react-rnd'
import { Grid } from '@mui/material'

import {
  FORM_ELEMENT_TEXT_ALIGN,
  FORM_ELEMENT_TYPE,
  FORM_FIELD_LABEL_POSITION,
  GRID_SIZE,
} from '../../../../../constants'
import { ResizeGridItemWrapper } from '../../../../../features/ResizeGridItemWrapper'
import { getElementPathByDroppableId, getLineById } from '../../../../../helpers'
import {
  useCreateContainerElementMutation,
  useCreateListControlFilterMutation,
  useDeleteContainerElementMutation,
  useDeleteListControlFilterMutation,
  useUpdateContainerElementMutation,
  useUpdateListControlFilterMutation,
} from '../../../../../redux/api/container.api'
import {
  AutocompleteOption,
  FormContainer,
  FormElement,
  GETListControlFilter,
} from '../../../../../types'
import { ElementContextMenu } from '../ActionMenu/ElementContextMenu'
import { TextPopover } from '../ActionMenu/TextPopover'
import { useActionMenu } from '../ActionMenu/useActionMenu'
import { FieldsConfigContext } from '../FieldsConfig'

import { Field } from './Field'

type DraggableFieldOnFormProps = {
  element: FormElement
  index: number
  countSizeRow: number
  pathToLine: string
  width: number
}

export const DraggableFieldOnForm: FC<DraggableFieldOnFormProps> = ({
  element,
  index,
  pathToLine,
  countSizeRow,
  width,
}) => {
  const { t } = useTranslation()
  const {
    objectCode,
    actions,
    containers,
    setContainers,
    activeMenuElementId,
    setActiveMenuElementId,
    isFetchingAvailableFields,
  } = useContext(FieldsConfigContext)
  const [deleteElement, { isLoading: isDeleteElementInProgress }] =
    useDeleteContainerElementMutation()
  const [updateElement, { isLoading: isUpdateElementInProgress }] =
    useUpdateContainerElementMutation()
  const [createElement, { isLoading: isCreateElementInProgress }] =
    useCreateContainerElementMutation()
  const [createFilter] = useCreateListControlFilterMutation()
  const [updateFilter] = useUpdateListControlFilterMutation()
  const [deleteFilter] = useDeleteListControlFilterMutation()

  const [currentSize, setCurrentSize] = useState(element.size)
  const [editingInProgress, setTextElementEditing] = useState(false)
  const [textTitle, setTextTitle] = useState('')
  const [textAlign, setTextAlign] = useState<FORM_ELEMENT_TEXT_ALIGN>(FORM_ELEMENT_TEXT_ALIGN.LEFT)
  const [action, setAction] = useState<AutocompleteOption | null>(null)
  const [elementTitleHidden, setElementTitleHidden] = useState(false)
  const [elementIsExpandable, setElementIsExpandable] = useState(false)
  const [labelPosition, setLabelPosition] = useState<FORM_FIELD_LABEL_POSITION>(
    FORM_FIELD_LABEL_POSITION.LEFT
  )

  const textElementCreationInProgress =
    (element.type === FORM_ELEMENT_TYPE.TEXT || element.type === FORM_ELEMENT_TYPE.ACTION_BUTTON) &&
    element.id === -1
  const isSmallSize = currentSize === 1
  const elementId = `elementId:${element.id}`

  const newItemRef = useRef<HTMLDivElement>(null)

  const minWidth = useMemo(() => width / GRID_SIZE, [width])
  const remains = useMemo(
    () => GRID_SIZE - (countSizeRow - currentSize),
    [countSizeRow, currentSize]
  )

  const actionOptions = useMemo(() => {
    return actions.map(({ id, title: label }) => ({ id, label })) || []
  }, [actions, action])

  useEffect(() => {
    if (element.title) {
      setTextTitle(element.title)
    }

    if (element.labelPosition) {
      setLabelPosition(element.labelPosition)
    }

    if (element.textAlign) {
      setTextAlign(element.textAlign)
    }

    if (element.titleHidden !== undefined) {
      setElementTitleHidden(element.titleHidden)
    }

    if (element.expandable !== undefined) {
      setElementIsExpandable(element.expandable)
    }

    if (element.type === FORM_ELEMENT_TYPE.ACTION_BUTTON && element.data) {
      const actionOption = actionOptions.find(action => action.id === element.data) ?? null
      setAction(actionOption)
    }
  }, [])

  const handleDeleteFieldOnForm = () => {
    const newContainers = structuredClone(containers) as FormContainer[]
    const [containerId, tabId, lineId] = getElementPathByDroppableId(pathToLine)
    const { line } = getLineById(newContainers, containerId, tabId, lineId)

    if (!line) {
      return
    }

    line.elements.splice(index, 1)

    deleteElement(element.id)
  }

  const getSize = (width: number): number => {
    const widthSize = Math.round(width / minWidth)
    // размер если widthSize больше свободного места в строке
    const sizeLargerRemains = widthSize >= remains ? remains : widthSize
    // результат в зависимости от единицы
    return widthSize > 1 ? sizeLargerRemains : 1
  }

  const handleResize: RndResizeCallback = (e, direction, ref, delta) => {
    const result = getSize(parseInt(ref.style.width, 10))

    setCurrentSize(result)
  }

  const handleResizeStop: RndResizeCallback = (e, direction, ref, delta) => {
    const [, , lineId] = getElementPathByDroppableId(pathToLine)

    const result = getSize(parseInt(ref.style.width, 10))

    if (result !== element.size) updateElement({ ...element, size: result, lineId })
  }

  const handleSetTextElementEditing = (value: boolean) => setTextElementEditing(value)

  const {
    popoverAnchorEl,
    menuPosition,
    isPopoverOpen,
    handleOpenPopover,
    handlePopoverClose,
    handleCancelPoppedEdit,
    handleElementContextClick,
    handleCloseMenu: handleFieldMenuClose,
    handleSetMenuPosition,
    handleSetPopoverAnchorEl,
  } = useActionMenu({ handleSetEditing: handleSetTextElementEditing })

  const onRightClickElement = (event: MouseEvent<HTMLDivElement>, elementId: number) => {
    event.preventDefault()
    handleFieldMenuClose()
    setActiveMenuElementId(elementId)
    handleElementContextClick(event)
  }

  const closeAllMenus = () => {
    setActiveMenuElementId(null)
    handleFieldMenuClose()
  }

  const handleFieldClick = (e: MouseEvent<HTMLDivElement>) => {
    if (element.type === FORM_ELEMENT_TYPE.SPACE) {
      return
    }

    onRightClickElement(e, element.id)
  }

  const getLineIdAndClosePopups = () => {
    if (menuPosition) {
      handleSetMenuPosition(null)
    }

    if (popoverAnchorEl) {
      handleSetPopoverAnchorEl(null)
    }

    const newContainers = structuredClone(containers) as FormContainer[]
    const [containerIndex, tabIndex, lineIndex] = getElementPathByDroppableId(pathToLine)
    const { line } = getLineById(newContainers, containerIndex, tabIndex, lineIndex)

    return line?.id
  }

  const handleSaveTextTitle = () => {
    if (!textTitle) {
      return
    }

    const lineId = getLineIdAndClosePopups()

    if (!lineId) {
      return
    }

    const { order, size, type } = element

    const newElement = {
      order,
      size,
      type,
      title: textTitle,
      data: action?.id,
      textAlign,
    }

    if (editingInProgress) {
      updateElement({
        ...newElement,
        lineId,
        id: element.id,
      })

      setTextElementEditing(false)
    }

    if (textElementCreationInProgress) {
      createElement({
        ...newElement,
        lineId,
      })
    }
  }

  const handleUpdateFieldData = ({
    labelPositionValue,
    titleHiddenValue,
    isExpandable,
  }: {
    labelPositionValue: FORM_FIELD_LABEL_POSITION
    titleHiddenValue: boolean
    isExpandable: boolean
  }) => {
    if (element.type !== FORM_ELEMENT_TYPE.FIELD && element.type !== FORM_ELEMENT_TYPE.VIEW) {
      return
    }

    const lineId = getLineIdAndClosePopups()

    if (!lineId) {
      return
    }

    const { order, size, type, data, title } = element

    const newElement = {
      order,
      size,
      type,
      data,
      title,
      labelPosition: labelPositionValue,
      titleHidden: titleHiddenValue,
      expandable: isExpandable,
    }

    const isDirty =
      element.labelPosition !== labelPositionValue ||
      element.titleHidden !== titleHiddenValue ||
      elementIsExpandable !== isExpandable

    if (isDirty) {
      setLabelPosition(labelPositionValue)
      setElementTitleHidden(titleHiddenValue)
      setElementIsExpandable(isExpandable)

      updateElement({
        ...newElement,
        lineId,
        id: element.id,
      })
    }
  }

  const handleCancelContainerCreationOrEdit = () => {
    if (textElementCreationInProgress) {
      const newContainers = structuredClone(containers) as FormContainer[]
      const [containerIndex, tabIndex, lineIndex] = getElementPathByDroppableId(pathToLine)
      const { line } = getLineById(newContainers, containerIndex, tabIndex, lineIndex)

      if (!line) {
        return
      }

      const textElementIndex = line?.elements.findIndex(element => element.id === -1)
      line?.elements.splice(textElementIndex, 1)

      setContainers(newContainers)
    }

    if (isPopoverOpen) {
      handleCancelPoppedEdit()
    }

    setTextElementEditing(false)
  }

  const handleSetTextTitle = (value: string) => setTextTitle(value)

  const handleSetAction = (value: AutocompleteOption) => setAction(value)

  const handleSetTextAlign = (value: FORM_ELEMENT_TEXT_ALIGN) => setTextAlign(value)

  const handleSetLabelPosition = (value: FORM_FIELD_LABEL_POSITION) => {
    handleUpdateFieldData({
      labelPositionValue: value,
      titleHiddenValue: false,
      isExpandable: elementIsExpandable,
    })
  }

  const handleSetElementTitleHidden = (value: boolean) => {
    handleUpdateFieldData({
      labelPositionValue: labelPosition,
      titleHiddenValue: value,
      isExpandable: elementIsExpandable,
    })
  }

  const handleSetIsExpandable = (value: boolean) => {
    handleUpdateFieldData({
      labelPositionValue: labelPosition,
      titleHiddenValue: elementTitleHidden,
      isExpandable: value,
    })
  }

  const handleSubmitFilter = (filter: GETListControlFilter) => {
    if (filter.id) {
      return updateFilter(filter)
    }

    return createFilter(filter)
  }

  const handleDeleteFilter = (id: number) => {
    deleteFilter(id)
  }

  return (
    <>
      <TextPopover
        action={action}
        actionOptions={actionOptions}
        displayActions={element.type === FORM_ELEMENT_TYPE.ACTION_BUTTON}
        displayAlignmentSettings={element.type === FORM_ELEMENT_TYPE.TEXT}
        handlePopoverClose={handlePopoverClose}
        handleSaveText={handleSaveTextTitle}
        handleSetAction={handleSetAction}
        handleSetText={handleSetTextTitle}
        handleSetTextAlign={handleSetTextAlign}
        popoverAnchorEl={textElementCreationInProgress ? newItemRef.current : popoverAnchorEl}
        staticTextTitle={element.title}
        textAlign={textAlign}
        textTitle={textTitle}
        handleCancelPoppedEdit={
          textElementCreationInProgress
            ? handleCancelContainerCreationOrEdit
            : handleCancelPoppedEdit
        }
        isDisableSaveTextButton={
          isCreateElementInProgress || isFetchingAvailableFields || isUpdateElementInProgress
        }
        isPopoverOpen={
          textElementCreationInProgress ? textElementCreationInProgress : isPopoverOpen
        }
      />
      {activeMenuElementId === element.id && (
        <ElementContextMenu
          displayDeleteOption={isSmallSize}
          displayExpandableOption={element.type !== FORM_ELEMENT_TYPE.FIELD}
          displayLabelAlignmentOption={element.type !== FORM_ELEMENT_TYPE.VIEW}
          elementIsExpandable={elementIsExpandable}
          elementTitleHidden={elementTitleHidden}
          handleDeleteFieldOnForm={handleDeleteFieldOnForm}
          handleFieldMenuClose={closeAllMenus}
          handleOpenEdit={handleOpenPopover}
          handleSetElementTitleHidden={handleSetElementTitleHidden}
          handleSetIsExpandable={handleSetIsExpandable}
          handleSetLabelPosition={handleSetLabelPosition}
          labelPosition={labelPosition}
          menuPosition={menuPosition}
          displayEditOption={
            element.type === FORM_ELEMENT_TYPE.TEXT ||
            element.type === FORM_ELEMENT_TYPE.ACTION_BUTTON
          }
          displayLabelParamsOption={
            element.type === FORM_ELEMENT_TYPE.FIELD || element.type === FORM_ELEMENT_TYPE.VIEW
          }
        />
      )}
      <Draggable draggableId={elementId} index={index}>
        {(provided, snapshot) => {
          const dragHandlePropsForContainer =
            isSmallSize && provided.dragHandleProps ? provided.dragHandleProps : undefined
          const dragHandlePropsForIcon =
            !isSmallSize && provided.dragHandleProps ? provided.dragHandleProps : undefined

          return (
            <Grid
              ref={provided.innerRef}
              item
              alignItems={'center'}
              {...provided.draggableProps}
              sx={{
                py: 0.5,
              }}
              onContextMenu={handleFieldClick}
            >
              <ResizeGridItemWrapper
                countSize={countSizeRow}
                disableResizing={element.type === FORM_ELEMENT_TYPE.VIEW}
                size={currentSize}
                width={width}
                onResize={handleResize}
                onResizeStop={handleResizeStop}
              >
                <Field
                  ref={newItemRef}
                  dragHandlePropsForContainer={dragHandlePropsForContainer}
                  dragHandlePropsForIcon={dragHandlePropsForIcon}
                  elementData={element.data}
                  elementId={element.id}
                  elementOuterParameters={element.formToLcConnections}
                  elementTitle={element.title}
                  elementType={element.type}
                  handleDeleteFieldOnForm={handleDeleteFieldOnForm}
                  isDeleteElementInProgress={isDeleteElementInProgress || isFetchingAvailableFields}
                  isEditOrPopup={textElementCreationInProgress && !isPopoverOpen}
                  isSmallSize={isSmallSize}
                  objectCode={objectCode}
                  textTitle={textTitle}
                  onDeleteFilter={handleDeleteFilter}
                  onSubmitFilter={handleSubmitFilter}
                />
              </ResizeGridItemWrapper>
            </Grid>
          )
        }}
      </Draggable>
    </>
  )
}
